/* ==========================================================================
   Fuel Injector Size Calculator
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-isc]');
  if (!root) return;

  /* Brake specific fuel consumption, pounds of fuel per horsepower per hour.
     Naturally aspirated gasoline engines sit near 0.50. Boosted engines run
     richer to control detonation. Ethanol carries less energy per pound so
     E85 needs far more mass flow for the same power. */
  var BSFC = {
    na:      0.50,
    boosted: 0.62,
    e85na:   0.72,
    e85b:    0.85
  };

  /* Gasoline flow conversion. cc/min is a volume rate, lb/hr a mass rate. */
  var CC_PER_LBHR = 10.5;

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  function compute(input) {
    if (!isFinite(input.hp) || input.hp <= 0) {
      return { ok: false, errors: ['Enter your target horsepower.'] };
    }
    var cyl = Math.round(input.cyl);
    if (!isFinite(cyl) || cyl < 1) {
      return { ok: false, errors: ['Enter the number of injectors.'] };
    }
    var duty = isFinite(input.duty) && input.duty > 0 ? Math.min(100, input.duty) / 100 : 0.85;
    var bsfc = BSFC[input.setup] || BSFC.na;

    var totalLbHr = input.hp * bsfc;
    var perInjector = totalLbHr / (cyl * duty);
    var perCc = perInjector * CC_PER_LBHR;

    var figures = [
      { label: 'Injector size needed', value: num(perInjector, 0) + ' lb/hr',
        sub: num(perCc, 0) + ' cc/min each, at ' + num(duty * 100, 0) + '% duty.' },
      { label: 'Total fuel flow', value: num(totalLbHr, 0) + ' lb/hr',
        sub: 'Across ' + cyl + ' injectors at ' + num(bsfc, 2) + ' BSFC.' }
    ];

    var rows = [
      { head: 'Requirement' },
      { label: 'Target power', value: comma(Math.round(input.hp)) + ' hp' },
      { label: 'BSFC used', value: num(bsfc, 2) },
      { label: 'Total fuel', value: num(totalLbHr, 0) + ' lb/hr' },
      { label: 'Injectors', value: String(cyl) },
      { label: 'Duty cycle', value: num(duty * 100, 0) + '%' },
      { label: 'Each injector', value: num(perInjector, 1) + ' lb/hr  (' + num(perCc, 0) + ' cc/min)' }
    ];

    var status, warnings = [];
    if (isFinite(input.have) && input.have > 0) {
      var supported = (input.have * cyl * duty) / bsfc;
      var dutyAtTarget = (totalLbHr / (input.have * cyl)) * 100;
      rows.push({ head: 'Your injectors' });
      rows.push({ label: 'Size', value: num(input.have, 0) + ' lb/hr  (' + num(input.have * CC_PER_LBHR, 0) + ' cc/min)' });
      rows.push({ label: 'Supports', value: comma(Math.round(supported)) + ' hp' });
      rows.push({ label: 'Duty at your target', value: num(dutyAtTarget, 0) + '%' });

      figures.push({ label: 'Your injectors support', value: comma(Math.round(supported)) + ' hp',
        sub: 'At ' + num(duty * 100, 0) + '% duty on ' + num(bsfc, 2) + ' BSFC.' });

      if (dutyAtTarget > 100) {
        status = { key: 'critical', label: 'Not enough injector',
                   note: 'Your target needs ' + num(dutyAtTarget, 0) + '% duty, which is impossible. The engine will run lean and fail.' };
      } else if (dutyAtTarget > 90) {
        status = { key: 'now', label: num(dutyAtTarget, 0) + '% duty, too high',
                   note: 'Above 90% duty the injector never fully closes and flow stops being predictable. Go larger.' };
      } else if (dutyAtTarget > 85) {
        status = { key: 'soon', label: num(dutyAtTarget, 0) + '% duty, marginal',
                   note: 'Right at the practical ceiling with no headroom for a hot day or a bad tank of fuel.' };
      } else {
        status = { key: 'good', label: num(dutyAtTarget, 0) + '% duty',
                   note: 'Comfortable. These injectors support ' + comma(Math.round(supported)) + ' hp at your duty limit.' };
      }
    } else {
      status = { key: 'good', label: num(perInjector, 0) + ' lb/hr',
                 note: num(perCc, 0) + ' cc/min each for ' + comma(Math.round(input.hp)) + ' hp.' };
    }

    if (input.setup === 'e85na' || input.setup === 'e85b') {
      warnings.push('E85 needs roughly 40% more fuel mass than gasoline for the same power. Size the pump and the lines for it too, not just the injectors.');
    }

    return { ok: true, errors: [], warnings: warnings, status: status, figures: figures, rows: rows };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "hp",
          "key": "p",
          "type": "number"
      },
      {
          "name": "cyl",
          "key": "n",
          "type": "number"
      },
      {
          "name": "setup",
          "key": "s",
          "type": "select"
      },
      {
          "name": "duty",
          "key": "d",
          "type": "number"
      },
      {
          "name": "have",
          "key": "h",
          "type": "number"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-isc__figure">'
        + '<div class="pc-isc__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-isc__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-isc__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-isc__row pc-isc__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-isc__row">'
        + '<span class="pc-isc__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-isc__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_ISC = { compute: compute };
})();
